import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBetaData } from '../contexts/BetaDataContext';
import { supabase } from '../supabaseClient';

/**
 * useAnnouncement — 公告文字（跑馬燈）
 *
 * marquee_text 由 BetaDataContext 的 departments 查詢附帶取得（不額外發 API）
 * Realtime 監聽更新 + updateMarquee 寫入
 */
const useAnnouncement = () => {
  const { viewingCompanyId, profile } = useAuth();
  const { companyMarquee: ctxCompanyMarquee, departmentMarquee: ctxDepartmentMarquee, loading: ctxLoading } = useBetaData();
  const [companyMarquee, setCompanyMarquee] = useState(null);
  const [departmentMarquee, setDepartmentMarquee] = useState(null);

  // 從 context 同步 marquee 資料
  useEffect(() => {
    if (!ctxLoading) {
      setCompanyMarquee(ctxCompanyMarquee);
      setDepartmentMarquee(ctxDepartmentMarquee);
    }
  }, [ctxCompanyMarquee, ctxDepartmentMarquee, ctxLoading]);

  // Realtime subscribe：公告更新時自動反映
  useEffect(() => {
    if (!viewingCompanyId) return;

    const channels = [];

    // 監聽公司跑馬燈
    const companyChannel = supabase
      .channel(`company-marquee-${viewingCompanyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'companies',
          filter: `id=eq.${viewingCompanyId}`,
        },
        (payload) => {
          setCompanyMarquee(payload.new.marquee_text ?? null);
        }
      )
      .subscribe();
    channels.push(companyChannel);

    // 監聯部門跑馬燈
    if (profile?.department_id) {
      const deptChannel = supabase
        .channel(`dept-marquee-${profile.department_id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'departments',
            filter: `id=eq.${profile.department_id}`,
          },
          (payload) => {
            setDepartmentMarquee(payload.new.marquee_text ?? null);
          }
        )
        .subscribe();
      channels.push(deptChannel);
    }

    return () => {
      channels.forEach((ch) => supabase.removeChannel(ch));
    };
  }, [viewingCompanyId, profile?.department_id]);

  const updateMarquee = useCallback(async (type, text) => {
    try {
      if (type === 'company') {
        if (!viewingCompanyId) return { error: '無公司資訊' };

        const { error } = await supabase
          .from('companies')
          .update({ marquee_text: text || null })
          .eq('id', viewingCompanyId);

        if (error) throw error;
        setCompanyMarquee(text || null);
      } else {
        const targetDepartmentId = profile?.department_id;
        if (!targetDepartmentId) return { error: '無部門資訊' };

        const { error } = await supabase
          .from('departments')
          .update({ marquee_text: text || null })
          .eq('id', targetDepartmentId);

        if (error) throw error;
        setDepartmentMarquee(text || null);
      }

      return {};
    } catch (err) {
      console.error('Error updating announcement:', err);
      return { error: err.message };
    }
  }, [viewingCompanyId, profile?.department_id]);

  return {
    companyMarquee,
    departmentMarquee,
    loading: ctxLoading,
    updateMarquee,
  };
};

export default useAnnouncement;
